import React, { useState } from 'react';
import {
  Modal,
  Banner,
  Button,
  Stack,
  ButtonGroup,
  Checkbox,
  Loader,
  Divider, Text,
} from '@chaser/components';
import pluralize from 'pluralize';
import { useHistory } from 'react-router';
import usePush from '../../../../hooks/usePush';
import ConnectService from '../../../../services/connect-service';
import SageIntacctLogo from '../../../../assets/logos/sageintacct.png';

const EntitySelectionModal = ({
  reconnect,
  entities,
  company,
  credentials,
  open,
  close,
  onClosed,
  onBack,
}) => {
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const history = useHistory();

  const { execute: connectExecute, error: connectError, loading: connectLoading } = usePush(
    ConnectService.connectSageIntacct,
    {
      errorToast: 'Failed to connect',
    },
  );

  const toggleEntity = entityId => {
    setSelectedEntityIds(prev =>
      prev.includes(entityId) ? prev.filter(id => id !== entityId) : [...prev, entityId],
    );
  };

  const handleConnect = async () => {
    const [err, data] = await connectExecute({
      ...credentials,
      entityIds: selectedEntityIds,
    });

    if (err || !data) return;

    if (!data?.length) {
      close();
      return history.push('/');
    }

    close();

    const allShortCodes = data.map(org => org.ShortCode);
    const organisationsParam = allShortCodes.join(',');
    history.push(`/?organisation=${allShortCodes[0]}&organisations=${organisationsParam}`);
  };

  const hasSelection = selectedEntityIds.length > 0;

  const getEntityName = entity => {
    const baseName = `${entity.id}--${entity.name}`;

    if (entity.disconnected) {
      return `${baseName} (disconnected)`;
    }

    if (entity.connected) {
      return `${baseName} (connected)`;
    }

    if (!entity.isActive) {
      return `${baseName} (inactive)`;
    }

    return baseName;
  };

  return (
    <Modal open={open} toggle={close} onClosed={onClosed}>
      <Modal.Body>
        <Stack gap="medium">
          <img
            src={SageIntacctLogo}
            alt="Sage Intacct"
            style={{ display: 'block', maxHeight: 40, margin: '0 auto' }}
          />

          <Divider />

          <h1 style={{ fontSize: 18, textAlign: 'center', fontWeight: 'bold' }}>
            Select entities to { reconnect ? 'reconnect' : 'connect' }
          </h1>

          {!reconnect && (
            <Banner intent="info" showCloseButton={false}>
              Please select one or more from the entities provided below to connect and continue
            </Banner>
          )}

          <Divider />

          <Stack gap="small">
            <b style={{ fontSize: 16 }}>{company.name}</b>
            {entities.map(entity => (
              <Checkbox
                key={entity.id}
                checked={selectedEntityIds.includes(entity.id) || entity.connected}
                disabled={!entity.isActive || entity.connected}
                onChange={() => toggleEntity(entity.id)}
                label={getEntityName(entity)}
              >
                {getEntityName(entity)}
              </Checkbox>
            ))}
          </Stack>

          {connectLoading && !connectError && <Loader />}
          {connectError && <Text color="Red">{connectError}</Text>}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={onBack} disabled={connectLoading}>
            Back
          </Button>
          <Button disabled={!hasSelection} loading={connectLoading} onClick={handleConnect}>
            {reconnect ? 'Reconnect' : 'Connect'} {pluralize('entity', selectedEntityIds.length)}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default EntitySelectionModal;
